import React, {useEffect, useState} from "react";
import {Button, Form, Modal} from "react-bootstrap";
import {ToastContainer} from "react-toastify";
import AppComponentCardHeader from "../../common/AppComponentCardHeader";
import LoadingDataDiv from "../../common/LoadingDataDiv";
import DeleteWarningModal from "../../common/DeleteWarningModal";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import useAuth from "../../../../hooks/useAuth";
import {toastError, toastSuccess} from "../../../../helpers/Toast";
import {hasPermission, PERMISSIONS as P} from "../../../../auth/permissions";
import PermissionMatrix from "../permissions/PermissionMatrix";
import {Table} from "../../../table/Table";
import {defaultFormState} from "../../../../defaults/Forms";
import {
  onCloseDeleteWarningDialog,
  onItemsListDeleteButtonClick
} from "../../../../helpers/ComponentHelper";
import "../../../../css/DictionaryForms.css";
import "../../../../css/Permissions.css";

const emptyRole = {id: null, name: "", description: "", systemRole: false, assignedUsersCount: 0};

const Roles = () => {
  const axiosPrivate = useAxiosPrivate();
  const {auth} = useAuth();
  const canManageRoles = hasPermission(auth?.permissions, P.ROLES_MANAGE);
  const canManagePermissions = hasPermission(auth?.permissions, P.PERMISSIONS_MANAGE);

  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allowDelete, setAllowDelete] = useState(null);
  const [currentFormState, setCurrentFormState] = useState(defaultFormState);
  const [currentItem, setCurrentItem] = useState(emptyRole);
  const [backupItem, setBackupItem] = useState(emptyRole);
  const [itemChanged, setItemChanged] = useState(false);

  const state = {
    itemsList: roles,
    setItems: setRoles,
    currentItem,
    setCurrentItem,
    currentFormState,
    setCurrentFormState,
    defaultItem: emptyRole,
    backupItem,
    setBackupItem,
    itemChanged,
    setItemChanged,
    setAllowDelete,
    setRelatedItems: null
  };

  const columns = [
    {label: "Id", accessor: "id", sortable: true, searchable: false},
    {label: "Nazwa", accessor: "name", sortable: true, searchable: true},
    {label: "Opis", accessor: "description", sortable: true, searchable: true},
    {label: "Systemowa", accessor: "systemRole", sortable: true, searchable: false, type: "bool"},
    {label: "Użytkownicy", accessor: "assignedUsersCount", sortable: true, searchable: false},
    {label: "Edycja", accessor: "editBtn", sortable: false, searchable: false},
    ...(canManageRoles ? [{label: "Usuń", accessor: "deleteBtn", sortable: false, searchable: false}] : [])
  ];

  const load = async () => {
    setLoading(true);
    try {
      const [rolesResponse, catalogResponse] = await Promise.all([
        axiosPrivate.get("/admin/role"),
        axiosPrivate.get("/admin/permissions/catalog")
      ]);
      setRoles(Array.isArray(rolesResponse.data) ? rolesResponse.data : []);
      setCatalog(Array.isArray(catalogResponse.data) ? catalogResponse.data : []);
    } catch (error) {
      toastError("Nie udało się pobrać definicji ról i uprawnień.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (currentFormState.showForm && currentFormState.formAddingDataMode) {
      setGrants([]);
    }
  }, [currentFormState.showForm, currentFormState.formAddingDataMode]);

  useEffect(() => {
    if (allowDelete !== null) {
      onItemsListDeleteButtonClick(
        currentFormState,
        setCurrentFormState,
        "rola",
        allowDelete,
        currentItem.name
      );
    }
  }, [allowDelete]);

  const getCompleteItem = async id => {
    const role = roles.find(item => item.id === id) || emptyRole;
    try {
      const response = await axiosPrivate.get(`/admin/permissions/roles/${id}`);
      setGrants(Array.isArray(response.data) ? response.data : []);
      return {...role};
    } catch (error) {
      toastError("Nie udało się pobrać uprawnień roli.");
      return {...role};
    }
  };

  const closeForm = () => {
    setCurrentFormState(prev => ({...prev, showForm: false, formAddingDataMode: false}));
    setCurrentItem(emptyRole);
    setBackupItem(emptyRole);
    setGrants([]);
  };

  const save = async () => {
    if (!canManageRoles && !canManagePermissions) return;
    if (!currentItem.id && !canManageRoles) return;
    if (canManageRoles && !currentItem.name?.trim()) {
      toastError("Nazwa roli jest wymagana.");
      return;
    }

    setSaving(true);
    try {
      let savedRole = currentItem;
      if (canManageRoles) {
        const payload = {name: currentItem.name.trim(), description: currentItem.description || ""};
        const roleResponse = currentItem.id
          ? await axiosPrivate.put(`/admin/role/${currentItem.id}`, payload)
          : await axiosPrivate.post("/admin/role", payload);
        savedRole = roleResponse.data;
      }

      if (canManagePermissions && savedRole?.id) {
        await axiosPrivate.put(`/admin/permissions/roles/${savedRole.id}`, grants);
      }

      toastSuccess("Zapisano ustawienia roli.");
      closeForm();
      await load();
    } catch (error) {
      toastError(error?.response?.data?.message || error?.response?.data || "Nie udało się zapisać roli.");
    } finally {
      setSaving(false);
    }
  };

  const checkRelatedItems = id => {
    const role = roles.find(item => item.id === id);
    const removable = !!role && !role.systemRole && Number(role.assignedUsersCount || 0) === 0;
    setAllowDelete(removable);
  };

  const remove = async () => {
    if (!canManageRoles || !currentItem?.id) return;
    try {
      await axiosPrivate.delete(`/admin/role/${currentItem.id}`);
      toastSuccess("Usunięto rolę.");
      onCloseDeleteWarningDialog({state});
      await load();
    } catch (error) {
      toastError(error?.response?.data?.message || error?.response?.data || "Nie udało się usunąć roli.");
    }
  };

  const addDataButtonProps = canManageRoles ? {
    setCurrentItem,
    setBackupItem,
    defaultItem: emptyRole,
    currentFormState,
    setCurrentFormState,
    formDescription: "",
    formHeader: "Nowa rola",
    buttonTitle: "Dodaj rolę"
  } : null;

  const permissionsDisabled = !canManagePermissions || currentItem.name?.replace(/^ROLE_/i, "").toUpperCase() === "ADMIN";

  return (
    <div id="layoutSidenav_content" className="dictionary-page">
      <ToastContainer/>
      <AppComponentCardHeader title="Definicje ról" buttonProps={addDataButtonProps}/>

      <div className="container-fluid px-4 py-3">
        <div className={roles.length > 0 ? "card shadow p-1 mb-5 bg-white rounded-2" : "card shadow mb-5 bg-transparent rounded-2"}>
          {loading ? (
            <LoadingDataDiv/>
          ) : roles.length > 0 ? (
            <Table
              rows={roles}
              columns={columns}
              state={state}
              checkRelatedItems={checkRelatedItems}
              formHeader="Edycja roli"
              relatedItemsUrl={null}
              getCompleteItem={getCompleteItem}
              exportFileName="role_uzytkownikow.xlsx"
            />
          ) : (
            <div className="warn_info">NIE ZNALEZIONO ŻADNYCH DANYCH</div>
          )}
        </div>
      </div>

      <Modal
        show={currentFormState.showForm}
        onHide={closeForm}
        backdrop="static"
        keyboard={false}
        className="dictionary-form-modal qstaff-role-modal"
        dialogClassName="dictionary-form-modal-dialog qstaff-role-permission-dialog"
      >
        <Modal.Header closeButton>
          <Modal.Title>{currentItem.id ? `Rola: ${currentItem.name}` : "Nowa rola"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="qstaff-role-fields">
            <Form.Group>
              <Form.Label>Nazwa</Form.Label>
              <Form.Control
                size="sm"
                value={currentItem.name || ""}
                disabled={currentItem.systemRole || !canManageRoles}
                onChange={event => setCurrentItem(value => ({...value, name: event.target.value}))}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Opis</Form.Label>
              <Form.Control
                size="sm"
                value={currentItem.description || ""}
                disabled={!canManageRoles}
                onChange={event => setCurrentItem(value => ({...value, description: event.target.value}))}
              />
            </Form.Group>
          </div>

          {currentItem.name?.replace(/^ROLE_/i, "").toUpperCase() === "ADMIN" && (
            <div className="qstaff-permission-admin-note">
              Administrator ma zawsze pełny dostęp do funkcji aplikacji. Uprawnienia tej roli są systemowe.
            </div>
          )}

          <PermissionMatrix
            catalog={catalog}
            grants={grants}
            onChange={setGrants}
            mode="role"
            disabled={permissionsDisabled}
          />
        </Modal.Body>

        <Modal.Footer className="qstaff-role-footer">
          <Button variant="secondary" size="sm" onClick={closeForm} disabled={saving}>
            {canManageRoles || canManagePermissions ? "Anuluj" : "Zamknij"}
          </Button>
          {(canManageRoles || canManagePermissions) && (
            <Button size="sm" onClick={save} disabled={saving}>
              {saving ? "Zapisywanie…" : "Zapisz"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <DeleteWarningModal state={state} onDelete={remove} deleteItemName="rola"/>
    </div>
  );
};

export default Roles;
